import * as React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { CircleIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { ScopeWrapper } from "@/components/ui/scope-wrapper";

function RadioGroup({
  className,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Root>) {
  return (
    <RadioGroupPrimitive.Root
      data-slot="radio-group"
      className={cn("grid lg:grid-cols-2 gap-2", className)}
      {...props}
    />
  );
}

function RadioGroupItem({
  className,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Item>) {
  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-group-item"
      className={cn(
        "border-input text-primary cursor-pointer focus-visible:border-ring focus-visible:ring-ring/20 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 aspect-square size-4 shrink-0 rounded-full border shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        data-slot="radio-group-indicator"
        className="relative flex items-center justify-center"
      >
        <CircleIcon className="fill-primary absolute top-1/2 left-1/2 size-2 -translate-x-1/2 -translate-y-1/2" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
}

/**
 * @example
 * import { Eye, Send } from "lucide-react"
 * import { Frame, FrameHeader, FrameTitle } from "@/components/ui/frame"
 * import { Label } from "@/components/ui/label"
 * import { RadioCard, RadioCardContent, RadioCardDescription, RadioCardItem, RadioGroup } from "@/components/ui/radio-group"
 *
 * export function MessagingMode({ value, onValueChange }: { value: string; onValueChange: (value: string) => void }) {
 *   return (
 *     <Frame>
 *       <FrameHeader>
 *         <FrameTitle>Messaging</FrameTitle>
 *       </FrameHeader>
 *       <RadioGroup value={value} onValueChange={onValueChange} className="lg:grid-cols-2">
 *         <RadioCard>
 *           <Send />
 *           <RadioCardContent>
 *             <Label htmlFor="mode-automatic">Automatic</Label>
 *             <RadioCardDescription>Amy writes and sends emails on your behalf.</RadioCardDescription>
 *           </RadioCardContent>
 *           <RadioCardItem value="automatic" id="mode-automatic" />
 *         </RadioCard>
 *         <RadioCard>
 *           <Eye />
 *           <RadioCardContent>
 *             <Label htmlFor="mode-approval">Approval</Label>
 *             <RadioCardDescription>Amy drafts each email; you send it.</RadioCardDescription>
 *           </RadioCardContent>
 *           <RadioCardItem value="approval" id="mode-approval" />
 *         </RadioCard>
 *       </RadioGroup>
 *     </Frame>
 *   )
 * }
 */
function RadioCard({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <ScopeWrapper>
    <div
      data-slot="radio-card"
      className={cn(
        "border-input hover:bg-sidebar has-data-[state=checked]:border-ring has-data-[state=checked]:ring-3 ring-primary/10 relative flex w-full items-start gap-2 rounded-lg border p-4 shadow-xs outline-none transition-all",
        "[&>svg]:size-4.5 [&>svg]:opacity-60 [&>svg]:shrink-0",
        // In a Frame the card is one of its panels: the panel's border and
        // shadow. The picked one keeps its ring outside the border, like
        // everywhere else.
        "in-data-[slot=frame]:border-border in-data-[slot=frame]:shadow-xs/5",
        className
      )}
      {...props}
    />
    </ScopeWrapper>
  );
}

function RadioCardItem({
  className,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Item>) {
  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-card-item"
      className={cn(
        "border-input text-primary cursor-pointer focus-visible:border-ring focus-visible:ring-ring/20 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 aspect-square size-4 shrink-0 rounded-full border shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
        "order-1 after:absolute after:inset-0 ml-auto",
        className
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        data-slot="radio-group-indicator"
        className="relative flex items-center justify-center"
      >
        <CircleIcon className="fill-primary absolute top-1/2 left-1/2 size-2 -translate-x-1/2 -translate-y-1/2" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
}

function RadioCardContent({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="radio-card-content"
      className={cn("grid grow gap-1", className)}
      {...props}
    />
  );
}

function RadioCardDescription({
  className,
  ...props
}: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="radio-card-description"
      className={cn("text-muted-foreground text-xs", className)}
      {...props}
    />
  );
}

export {
  RadioGroup,
  RadioGroupItem,
  RadioCard,
  RadioCardItem,
  RadioCardContent,
  RadioCardDescription,
};